var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { BaseAuthenticationClient } from './BaseAuthenticationClient';
import { uploadFile, xhrUpload } from '../utils';
/**
 * @class MfaAuthenticationClient
 * @description This client is used for binding TOTP MFA authenticator, unbinding TOTP MFA authenticator, binding user face, unbinding face, and user secondary authentication.
 *
 * binding TOTP MFA authenticator：
 *
 * \`\`\`javascript
 * import { AuthenticationClient } from "approw-js-sdk"
 * const authenticationClient = new AuthenticationClient({
 *    appId: "YOUR_APP_ID",
 * })
 * await authenticationClient.mfa.assosicateMfaAuthenticator({authenticatorType: 'totp'})
 * \`\`\`
 *
 * Verify MFA secondary password：
 *
 * \`\`\`javascript
 * import { AuthenticationClient } from "approw-js-sdk"
 * const authenticationClient = new AuthenticationClient({
 *    appId: "YOUR_APP_ID",
 * })
 * await authenticationClient.mfa.verifyTotpMfa({totp: '112233', mfaToken: 'xxx'})
 * \`\`\`
 *
 * @name MfaAuthenticationClient
 */
var MfaAuthenticationClient = /** @class */ (function () {
    function MfaAuthenticationClient(options, tokenProvider, httpClient) {
        this.options = options;
        this.tokenProvider = tokenProvider;
        this.httpClient = httpClient;
        this.baseClient = new BaseAuthenticationClient(options);
    }
    /**
     * @name getMfaAuthenticators
     * @name_zh Get an MFA authenticator
     * @description Get an MFA authenticator
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.getMfaAuthenticators({ type: 'totp' })
     *
     * @returns {Promise<IMfaAuthenticators>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.getMfaAuthenticators = function (options) {
        if (options === void 0) { options = { type: 'totp', source: 'SELF' }; }
        return __awaiter(this, void 0, void 0, function () {
            var type, mfaToken, source, api, headers, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        type = options.type, mfaToken = options.mfaToken, source = options.source;
                        api = this.baseClient.appHost + "/api/v2/mfa/authenticator";
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'GET',
                                url: api,
                                params: {
                                    type: type,
                                    source: source
                                },
                                headers: headers
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name assosicateMfaAuthenticator
     * @name_zh Request MFA QR code and key information
     * @description Request MFA QR code and key information
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.assosicateMfaAuthenticator({ authenticatorType: 'totp' })
     *
     * @returns {Promise<IMfaAssociation>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.assosicateMfaAuthenticator = function (options) {
        if (options === void 0) { options = { authenticatorType: 'totp', source: 'SELF' }; }
        return __awaiter(this, void 0, void 0, function () {
            var authenticatorType, mfaToken, source, headers, api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        authenticatorType = options.authenticatorType, mfaToken = options.mfaToken, source = options.source;
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        api = this.baseClient.appHost + "/api/v2/mfa/totp/associate";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    authenticator_type: authenticatorType,
                                    source: source
                                },
                                headers: headers
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name deleteMfaAuthenticator
     * @name_zh Unbind MFA
     * @description Unbind MFA
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.deleteMfaAuthenticator()
     *
     * @returns {Promise<IMfaDeleteAssociation>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.deleteMfaAuthenticator = function () {
        return __awaiter(this, void 0, void 0, function () {
            var api;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/mfa/totp/associate";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'DELETE',
                                url: api
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, {
                                code: 200,
                                message: 'TOTP MFA Unbind successfully'
                            }];
                }
            });
        });
    };
    /**
     * @name confirmAssosicateMfaAuthenticator
     * @name_zh Confirm binding MFA
     * @description Confirm binding MFA
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.confirmAssosicateMfaAuthenticator({ authenticatorType: 'totp', totp: '112233' })
     *
     * @returns {Promise<IMfaConfirmAssociation>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.confirmAssosicateMfaAuthenticator = function (options) {
        if (options === void 0) { options = { authenticatorType: 'totp', source: 'SELF' }; }
        return __awaiter(this, void 0, void 0, function () {
            var api, authenticatorType, totp, source, mfaToken, headers;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/mfa/totp/associate/confirm";
                        authenticatorType = options.authenticatorType, totp = options.totp, source = options.source, mfaToken = options.mfaToken;
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    authenticator_type: authenticatorType,
                                    totp: totp,
                                    source: source
                                }
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, { code: 200, message: 'TOTP MFA bind successfully' }];
                }
            });
        });
    };
    /**
     * @name verifyTotpMfa
     * @name_zh Verify the MFA password for the second verification
     * @description Verify the MFA password for the second verification
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.verifyTotpMfa({ authenticatorType: 'totp', totp: '112233' })
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.verifyTotpMfa = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/mfa/totp/verify";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    totp: options.totp
                                },
                                headers: {
                                    authorization: 'Bearer ' + options.mfaToken
                                }
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name verifyAppSmsMfa
     * @name_zh Verify the secondary verification MFA SMS verification code
     * @description Verify the secondary verification MFA SMS verification code
     *
     * @param {object} options
     * @param {string} options.phone User phone number
     * @param {string} options.code SMS verification code
     * @param {string} options.token MfaToken returned by the login interface
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.verifySmsMfa({ mfaToken: 'xxxxxx', phone: '173xxxxxxxx' })
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.verifyAppSmsMfa = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/applications/mfa/sms/verify";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    phone: options.phone,
                                    code: options.code
                                },
                                headers: {
                                    authorization: 'Bearer ' + options.mfaToken
                                }
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name verifyAppEmailMfa
     * @name_zh Check the second verification MFA email verification code
     * @description Check the second verification MFA email verification code
     *
     * @param {object} options
     * @param {string} options.email user email
     * @param {string} options.code email verification code
     * @param {string} options.token MfaToken returned by the login interface
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.verifyAppEmailMfa({ mfaToken: 'xxxx', email: 'example@approw.com' })
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.verifyAppEmailMfa = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/applications/mfa/email/verify";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    email: options.email,
                                    code: options.code
                                },
                                headers: {
                                    authorization: 'Bearer ' + options.mfaToken
                                }
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name phoneOrEmailBindable
     * @name_zh Check whether the phone number or email has been bound
     * @description When the phone or email MFA login is required, and the user has not bound the phone or email address, the user can first enter the phone number or email address, use this interface to first check whether the phone or email address can be bound, and then perform MFA verification.
     *
     * @param {object} options
     * @param {string} [options.mfaToken] MfaToken returned by the backend
     * @param {string} [options.phone] Phone number to be detected
     * @param {string} [options.email] Email address to be detected
     *
     * @example
     *
     * authenticationClient.phoneOrEmailBindable({
     *  phone: '173xxxxxxxx',
     *  mfaToken: 'xxxxx'
     * })
     *
     * @returns {Promise<boolean>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.phoneOrEmailBindable = function (_a) {
        var phone = _a.phone, email = _a.email, mfaToken = _a.mfaToken;
        return __awaiter(this, void 0, void 0, function () {
            var api;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/applications/mfa/check";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    email: email,
                                    phone: phone
                                },
                                headers: {
                                    authorization: 'Bearer ' + mfaToken
                                }
                            })];
                    case 1: return [2 /*return*/, _b.sent()];
                }
            });
        });
    };
    /**
     * @name verifyTotpRecoveryCode
     * @name_zh Verify the second verification MFA recovery code
     * @description Verify the second verification MFA recovery code
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const authenticators = await authenticationClient.mfa.verifyTotpRecoveryCode({ authenticatorType: 'totp', totp: '112233' })
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.verifyTotpRecoveryCode = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/mfa/totp/recovery";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    recoveryCode: options.recoveryCode
                                },
                                headers: {
                                    authorization: 'Bearer ' + options.mfaToken
                                }
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name associateFaceByUrl
     * @name_zh Binding face with picture URL
     * @description Binding face with picture URL
     *
     * @param {object} options
     * @param {string} options.baseFace Basic face photo
     * @param {string} options.compareFace Face photo address for comparison
     * @param {string} [options.mfaToken] If you bind the face during secondary authentication, you need to pass in mfaToken
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const user = await authenticationClient.mfa.associateFace({ photoA: 'http://example.com/photo/imgA.jpg', photoB: 'http://example.com/photo/imgB.jpg', mfaToken: 'xxxxxxxxxxxx' })
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.associateFaceByUrl = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var baseFace, compareFace, mfaToken, headers, api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        baseFace = options.baseFace, compareFace = options.compareFace, mfaToken = options.mfaToken;
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        api = this.baseClient.appHost + "/api/v2/mfa/face/associate";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    photoA: baseFace,
                                    photoB: compareFace,
                                    isExternal: true
                                },
                                headers: headers
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name associateFaceByLocalFile
     * @name_zh Bind faces by uploading local files
     * @description Bind faces by uploading local files
     *
     * @param {string} [mfaToken] If you bind the face during secondary authentication, you need to pass in mfaToken
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const user = await authenticationClient.mfa.associateFaceByLocalFile('xxxxxxxxxxx')
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.associateFaceByLocalFile = function (mfaToken) {
        return __awaiter(this, void 0, void 0, function () {
            var headers, uploadedKey, key, e_1, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, uploadFile({
                                // Alibaba Cloud supports these
                                accept: '.jpeg,.jpg,.png,.bmp',
                                url: this.baseClient.appHost + "/api/v2/upload?folder=face-photo&private=true"
                            })];
                    case 2:
                        key = (_a.sent()).key;
                        uploadedKey = key;
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        this.options.onError(e_1.code, e_1.message);
                        return [3 /*break*/, 4];
                    case 4: return [4 /*yield*/, this.httpClient.request({
                            method: 'POST',
                            url: this.baseClient.appHost + "/api/v2/mfa/face/associate",
                            data: {
                                photoA: uploadedKey,
                                photoB: uploadedKey
                            },
                            headers: headers
                        })];
                    case 5:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name associateFaceByBlob
     * @name_zh Bind the face by passing in the Blob object
     * @description Bind the face by passing in the Blob object
     *
     * @param {object} options
     * @param {string} options.baseFace Basic face data Blob object
     * @param {string} options.compareFace Face data Blob object for comparison
     * @param {string} [options.mfaToken] If you bind the face during secondary authentication, you need to pass in mfaToken
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const user = await authenticationClient.mfa.associateFaceByBlob({blobA: Blob, blobB: Blob, mfaToken: 'xxx'})
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.associateFaceByBlob = function (opts) {
        return __awaiter(this, void 0, void 0, function () {
            var baseFace, compareFace, mfaToken, headers, photoA, photoB, uploadUrl, keyA, keyB, e_2, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        baseFace = opts.baseFace, compareFace = opts.compareFace, mfaToken = opts.mfaToken;
                        headers = {};
                        if (mfaToken) {
                            headers.authorization = "Bearer " + mfaToken;
                        }
                        uploadUrl = this.baseClient.appHost + "/api/v2/upload?folder=face-photo&private=true";
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, , 7]);
                        return [4 /*yield*/, xhrUpload(baseFace, uploadUrl)];
                    case 2:
                        keyA = (_a.sent()).key;
                        photoA = keyA;
                        if (!compareFace) return [3 /*break*/, 4];
                        return [4 /*yield*/, xhrUpload(compareFace, uploadUrl)];
                    case 3:
                        keyB = (_a.sent()).key;
                        photoB = keyB;
                        return [3 /*break*/, 5];
                    case 4:
                        photoB = keyA;
                        _a.label = 5;
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        e_2 = _a.sent();
                        this.options.onError(e_2.code, e_2.message);
                        return [3 /*break*/, 7];
                    case 7: return [4 /*yield*/, this.httpClient.request({
                            method: 'POST',
                            url: this.baseClient.appHost + "/api/v2/mfa/face/associate",
                            data: {
                                photoA: photoA,
                                photoB: photoB
                            },
                            headers: headers
                        })];
                    case 8:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    /**
     * @name verifyFaceMfa
     * @name_zh Detect secondary login face verification
     * @description Detect secondary login face verification
     *
     * @param {string} photo Face photo address
     * @param {string} mfaToken MfaToken returned by Approw during the second verification
     *
     * @example
     * const authenticationClient = new AuthenticationClient({
     *    appId: "YOUR_APP_ID",
     * })
     * const user = await authenticationClient.mfa.verifyFaceMfa('http://example.com/photo/photo.jpg')
     *
     * @returns {Promise<User>}
     * @memberof MfaAuthenticationClient
     */
    MfaAuthenticationClient.prototype.verifyFaceMfa = function (photo, mfaToken) {
        return __awaiter(this, void 0, void 0, function () {
            var api, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        api = this.baseClient.appHost + "/api/v2/mfa/face/verify";
                        return [4 /*yield*/, this.httpClient.request({
                                method: 'POST',
                                url: api,
                                data: {
                                    photo: photo
                                },
                                headers: {
                                    authorization: "Bearer " + mfaToken
                                }
                            })];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    return MfaAuthenticationClient;
}());
export { MfaAuthenticationClient };
//# sourceMappingURL=data:application/json;base64,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